import { useState } from "react";
import { useInView } from "../../../hooks/useInView";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const StarRating = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className={`ri-star-fill text-sm ${i < count ? "text-yellow-400" : "text-zinc-600"}`}></i>
    ))}
  </div>
);

const TestimonialsSection = () => {
  const { ref, inView } = useInView();
  const [current, setCurrent] = useState(0);
  const { content } = useSiteContent();
  const section = content.home.testimonials;

  const visibleCount = 3;
  const maxIndex = Math.max(0, section.items.length - visibleCount);

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(maxIndex, c + 1));

  return (
    <section id="testimonials" className="bg-zinc-900 py-20 md:py-28" {...editableAttrs("home.testimonials", "object")}>
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.testimonials.badge", "text")}>
              {section.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 section-title-line" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              <span {...editableAttrs("home.testimonials.titleLines.0", "text")}>{section.titleLines[0]}</span>
              <br />
              <span {...editableAttrs("home.testimonials.titleLines.1", "text")}>{section.titleLines[1]}</span>
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={prev}
              disabled={current === 0}
              className="w-10 h-10 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:border-red-600 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              onClick={next}
              disabled={current >= maxIndex}
              className="w-10 h-10 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:border-red-600 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" {...editableAttrs("home.testimonials.items", "array")}>
          {section.items.slice(current, current + visibleCount).map((t, idx) => (
            <div
              key={`${t.name}-${idx}`}
              className="bg-zinc-800 p-6 flex flex-col"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 0.5s ease ${idx * 0.1}s, transform 0.5s ease ${idx * 0.1}s`,
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center ${t.color} flex-shrink-0`}>
                  <span className="text-white text-xs font-bold">{t.avatar}</span>
                </div>
                <div>
                  <div className="text-white font-semibold text-sm" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                    {t.name}
                  </div>
                  <div className="text-zinc-500 text-xs">{t.car}</div>
                </div>
                <div className="ml-auto">
                  <i className="ri-google-fill text-zinc-500 text-base"></i>
                </div>
              </div>

              <StarRating count={t.rating} />
              <p className="text-zinc-300 text-sm leading-relaxed mt-4 flex-1">&ldquo;{t.text}&rdquo;</p>
              <div className="text-zinc-600 text-xs mt-4 pt-4 border-t border-zinc-700">{t.date}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-red-600 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white text-2xl md:text-3xl font-bold" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.testimonials.ctaTitle", "textarea")}>
              {section.ctaTitle}
            </h3>
            <p className="text-red-100 text-sm mt-1" {...editableAttrs("home.testimonials.ctaDescription", "text")}>
              {section.ctaDescription}
            </p>
          </div>
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-white text-red-600 hover:bg-zinc-100 px-8 py-3.5 font-bold text-sm tracking-widest whitespace-nowrap transition-colors cursor-pointer"
            style={{ fontFamily: "Rajdhani, sans-serif" }}
            {...editableAttrs("home.testimonials.ctaButtonLabel", "text")}
          >
            {section.ctaButtonLabel}
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
